import {useCallback, useEffect, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'
import QrTypeSelector from '../components/qrGenerator/QrTypeSelector'
import QrDataForm from '../components/qrGenerator/QrDataForm'
import QrStylePanel from '../components/qrGenerator/QrStylePanel'
import QrLogoPicker from '../components/qrGenerator/QrLogoPicker'
import QrPreview from '../components/qrGenerator/QrPreview'
import QrDownloadPanel from '../components/qrGenerator/QrDownloadPanel'
import Seo from '../components/seo/Seo'
import {SITE_URL} from '../components/seo/seoConstants'
import {CtaSection} from '../components/seo/seoSections'
import {trackEvent} from '../../common/analytics/trackEvent'
import type {QrType} from '../../common/constants/qrTypes'
import {buildQrPayload, DEFAULT_QR_FIELDS, type QrFieldsState} from '../../common/utils/qrPayload'
import {DEFAULT_QR_LOGO, DEFAULT_QR_STYLE} from '../../common/constants/qrStyleOptions'
import type {QrLogoState, QrStyleState} from '../../common/utils/qrRenderer'
import '../components/seo/SeoPage.css'
import './QrGeneratorPage.css'

const QR_JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'Ecranify QR Code Generator',
  applicationCategory: 'DesignApplication',
  operatingSystem: 'Web browser',
  url: `${SITE_URL}/qr-generator`,
  description:
    'Free browser-based QR code generator with color and gradient styling, a center logo, and PNG/JPG/WEBP/SVG export.',
  offers: {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'USD',
  },
}

const QrGeneratorPage = () => {
  const {t} = useTranslation()

  const [type, setType] = useState<QrType>('url')
  const [fields, setFields] = useState<QrFieldsState>(DEFAULT_QR_FIELDS)
  const [style, setStyle] = useState<QrStyleState>(DEFAULT_QR_STYLE)
  const [logo, setLogo] = useState<QrLogoState>(DEFAULT_QR_LOGO)

  useEffect(() => {
    trackEvent('qr_generator_viewed')
  }, [])

  const handleFieldsChange = useCallback(<T extends QrType>(fieldType: T, patch: Partial<QrFieldsState[T]>) => {
    setFields((prev) => ({...prev, [fieldType]: {...prev[fieldType], ...patch}}))
  }, [])

  const handleStyleChange = useCallback((patch: Partial<QrStyleState>) => {
    setStyle((prev) => ({...prev, ...patch}))
  }, [])

  const handleLogoChange = useCallback((patch: Partial<QrLogoState>) => {
    setLogo((prev) => ({...prev, ...patch}))
  }, [])

  const payload = useMemo(() => buildQrPayload(type, fields), [type, fields])

  return (
    <div className="qrg-root seo-root">
      <Seo
        title="Free QR Code Generator — Custom Colors, Gradients & Logo | Ecranify"
        description="Create URL, WiFi, vCard, event and other QR codes for free. Style them with solid colors or gradients, add a logo in the center, and export as PNG, JPG, WEBP or SVG — all in your browser."
        canonicalPath="/qr-generator"
        jsonLd={[QR_JSON_LD]}
      />
      <div className="qrg-grid" aria-hidden />

      <main className="qrg-page">
        <header className="qrg-hero">
          <span className="qrg-eyebrow">
            <span className="qrg-eyebrow-dot" />
            {t('qrGenerator.hero.eyebrow')}
          </span>
          <h1 className="qrg-title">
            {t('qrGenerator.hero.titlePrefix')} <em>{t('qrGenerator.hero.titleEm')}</em>
          </h1>
          <p className="qrg-lede">{t('qrGenerator.hero.subtitle')}</p>
        </header>

        <div className="qrg-layout">
          <section className="qrg-col qrg-col-source">
            <h2 className="qrg-section-title">{t('qrGenerator.sections.type')}</h2>
            <QrTypeSelector type={type} onChange={setType} />
            <h2 className="qrg-section-title">{t('qrGenerator.sections.content')}</h2>
            <QrDataForm type={type} fields={fields} onFieldsChange={handleFieldsChange} />
          </section>

          <section className="qrg-col qrg-col-preview">
            <h2 className="qrg-section-title">{t('qrGenerator.sections.preview')}</h2>
            <QrPreview data={payload} style={style} logo={logo} />
            <QrDownloadPanel data={payload} style={style} logo={logo} />
          </section>

          <section className="qrg-col qrg-col-export">
            <h2 className="qrg-section-title">{t('qrGenerator.sections.style')}</h2>
            <QrStylePanel style={style} onChange={handleStyleChange} />
            <h2 className="qrg-section-title">{t('qrGenerator.sections.logo')}</h2>
            <QrLogoPicker logo={logo} onChange={handleLogoChange} />
          </section>
        </div>

        <section className="qrg-seo">
          <h2 className="qrg-seo-title">{t('qrGenerator.seo.title')}</h2>
          <p className="qrg-seo-body">{t('qrGenerator.seo.body1')}</p>
          <p className="qrg-seo-body">{t('qrGenerator.seo.body2')}</p>
        </section>

        <CtaSection cta={{title: t('qrGenerator.cta.title'), body: t('qrGenerator.cta.body')}} />
      </main>
    </div>
  )
}

export default QrGeneratorPage
